import { UserData } from "../context/UserContext";

const API_ROOT = process.env.REACT_APP_BASE_API + '/';

export const ASSUMPTION_REFUSED_EVENT = 'neoni:assumption-refused';
export const SESSION_EXPIRED_EVENT = 'neoni:session-expired';
export const ACCESS_AUDIT_UNAVAILABLE_EVENT = 'neoni:access-audit-unavailable';
export const READ_ONLY_ACCESS_EVENT = 'neoni:read-only-access';
export const ACCOUNT_LOCKED_EVENT = 'neoni:account-locked';
// Issue #759: a shared, call-site-agnostic signal for "the server could not be reached at all"
// (as opposed to it responding with an auth/permission failure). UserContext's periodic session
// recheck reports into this too, so every caller of the shared fetch layer - and any future push
// channel fallback (#767) - reads the same connection-health signal instead of re-deriving it.
export const CONNECTION_LOST_EVENT = 'neoni:connection-lost';
export const CONNECTION_RESTORED_EVENT = 'neoni:connection-restored';

// COMPANY_LOCKED is excluded: the organisation picker and dashboard banner already handle it.
export const ACCOUNT_LOCK_CODES = ['ADMIN_LOCKED', 'USER_LOCKED'];

// Issue #759 (security review): bumped by UserContext.tsx's clearUserData() - the single shared
// place every sign-out path goes through - every time the session is cleared. A request captures
// the generation it started in; if that no longer matches by the time the response arrives, the
// request belongs to an abandoned session and must not be allowed to repopulate Redux (a thunk's
// `.fulfilled`/`.rejected`) or hand data back to a caller that may itself have already unmounted.
// See request()'s generation checks below.
let sessionGeneration = 0;
export function bumpSessionGeneration() {
    sessionGeneration += 1;
}
// Issue #759 (final review F4): UserContext.tsx has two fetch() call sites of its own (the
// bootstrap/assumption `/me` fetch and the periodic focus/visibility recheck) that sit outside
// this module's request()/form() and so cannot see the private `sessionGeneration` variable
// directly - exposed read-only so they can apply the same generation guard before acting on a
// response (e.g. before setUserData, or before signing out for a stale 401/403).
export function getSessionGeneration() {
    return sessionGeneration;
}

// Optional cancellation is used by logout and access-scoped requests.
export type RequestOptions = {
    signal?: AbortSignal;
};

export type TrackedMutationOptions = RequestOptions & {
    onSubmitted?: () => void;
};

class SerializableApiError extends Error {
    status?: number;
    statusText?: string;
    body?: any;

    constructor(message: string, status?: number, statusText?: string, body?: any) {
        super(message);
        this.name = 'SerializableApiError';
        this.status = status;
        this.statusText = statusText;
        this.body = body;
    }
}

// Issue #759 (final review F2/F5): shared by every call site below (request() and form()) so a
// session expiry, account lock, lost connection, or abandoned-generation response is handled
// identically regardless of whether the call went through JSON (request()) or multipart (form())
// - the whole point of a "shared handler" is that there is exactly one place this logic lives.
async function fetchAndSignal(url: string, options: RequestInit, requestGeneration: number): Promise<Response> {
    let response: Response;
    try {
        response = await fetch(url, options);
    } catch (networkError) {
        // Issue #857 (item 5): a deliberate client-side cancellation (logout's AbortController
        // firing once its own timeout elapses - see lib/logout.ts) is not a real connectivity
        // problem, so it must not flip the shared connection-health signal. Checked via the
        // signal's own `aborted` flag rather than the error's name/type, so this also covers a
        // misbehaving test double (or future fetch polyfill) that rejects with something other
        // than a real AbortError after the signal fires. Still rethrown either way so the
        // caller (withTimeout) sees the failure.
        if (!options.signal?.aborted && requestGeneration !== sessionGeneration) {
            return new Promise(() => {});
        }
        if (!options.signal?.aborted) {
            // Issue #759: the server was never reached at all (offline, DNS failure, timeout) -
            // this is distinct from it responding with an error status. Flag it via the shared
            // connection-health signal so a "connection lost" indicator can replace any stale
            // data on screen, then rethrow so the caller still sees the failure (never swallowed).
            window.dispatchEvent(new Event(CONNECTION_LOST_EVENT));
        }
        throw networkError;
    }
    // Issue #857 (item 5): same reasoning as the abort branch above, for the success path - a
    // late resolution that arrives after the request was already deliberately aborted must not
    // report the connection as restored either.
    if (!options.signal?.aborted && requestGeneration !== sessionGeneration) {
        return new Promise(() => {});
    }
    if (!options.signal?.aborted) {
        // Reaching this point means the server responded at all (any status), proving the
        // connection is up even if the request itself failed for auth/permission reasons below.
        window.dispatchEvent(new Event(CONNECTION_RESTORED_EVENT));
    }
    return response;
}

async function handleResponse(response: Response, requestGeneration: number, returnErrorBody = false, signal?: AbortSignal): Promise<any> {
    // #882 (item 4): checked before anything else below - a response that resolved after its own
    // request was deliberately aborted (logout's AbortController - see lib/logout.ts - or a
    // misbehaving test double/polyfill that resolves instead of rejecting post-abort) must not be
    // allowed to run any of this function's side effects: body parsing, the redirectTo
    // navigation, or any coded-403/503 event dispatch. fetchAndSignal already suppresses the
    // connection-lost/-restored events for this case; this is the equivalent guarantee for every
    // other side effect a response can trigger. Ordinary (unsignalled) requests are unaffected -
    // `signal` is undefined for them, so `signal?.aborted` is always false.
    if(signal?.aborted) {
        throw new DOMException('The operation was aborted.', 'AbortError');
    }
    if(!response.ok) {
        // Issue #759 (final review F5): checked before the body is even parsed, and before any of
        // the coded-403/503 branches below can dispatch their event or perform the redirectTo
        // side effect - a response against a session that was cleared while it was in flight
        // belongs to an abandoned session, so it must not fire a SESSION_EXPIRED/ACCOUNT_LOCKED/
        // etc. event (or navigate anywhere) that a fresh, just-logged-in session would otherwise
        // be wrongly affected by. Previously this check only ran in the generic-error fallback
        // below every coded branch, so a stale coded-403 response could still fire its event.
        if(requestGeneration !== sessionGeneration) {
            return new Promise(() => {});
        }

        let responseBody;
        try {
            responseBody = await response.json();
        } catch(_) {
            // If response is not JSON, continue with basic error
        }

        // #883 (security review, findings 1-2): re-checked after the body await, which is the
        // real race the entry-time checks above cannot cover - a chunked/slow-trailing error body
        // (or a misbehaving test double/polyfill) can resolve after the session was cleared
        // (generation bump) or the request was deliberately aborted while still in flight. Both
        // outcomes of the try/catch above land here (including a rejected/non-JSON body), so a
        // swallowed parse failure cannot slip past this guard either - it still re-throws the
        // abort, or never settles for a stale generation, before reaching redirectTo or any
        // coded-403/503 event dispatch below.
        if(signal?.aborted) {
            throw new DOMException('The operation was aborted.', 'AbortError');
        }
        if(requestGeneration !== sessionGeneration) {
            return new Promise(() => {});
        }

        if(responseBody?.redirectTo) {
            window.location = responseBody.redirectTo;
        }

        // Throw serializable error
        const error = new SerializableApiError(
            'HTTP error',
            response.status,
            response.statusText,
            responseBody
        );
        // Issue #759: a bare 401 is deliberately NOT treated as session-expired here. Unlike
        // every other branch below (which key off a specific coded body), this backend uses a
        // bare 401 across dozens of controllers for unrelated, already-authenticated outcomes
        // - wrong login credentials, an expired password-reset link, permission-denied on a
        // specific resource (e.g. CompaniesController), and plain validation errors - none of
        // which mean "your session is gone". The backend's actual session-expiry signal is
        // always the coded 403 below (`SESSION_EXPIRED`); treating a bare 401 the same way
        // would silently hang those other flows (never resolving the caller's promise) and
        // force a false sign-out + Redux wipe on a simple permission or validation error.
        // See the #759 implementation report for the evidence; revisit only alongside a
        // backend change that gives a true "unauthenticated" 401 its own distinct code.
        if(response.status === 403 && responseBody?.code === 'ASSUMPTION_NOT_PERMITTED') {
            // UserProvider drops the assumption and navigates away; never settling keeps the
            // page that made this call from handling (or crashing on) the refusal.
            window.dispatchEvent(new Event(ASSUMPTION_REFUSED_EVENT));
            return new Promise(() => {});
        }
        if(response.status === 403 && responseBody?.code === 'SESSION_EXPIRED') {
            // UserProvider returns the user to the login screen; the caller must not render an error.
            window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
            return new Promise(() => {});
        }
        if(response.status === 403 && ACCOUNT_LOCK_CODES.includes(responseBody?.code)) {
            // UserProvider signs the user out and returns them to login with an on-hold message.
            window.dispatchEvent(new Event(ACCOUNT_LOCKED_EVENT));
            return new Promise(() => {});
        }
        if(response.status === 503 && responseBody?.code === 'ACCESS_AUDIT_UNAVAILABLE') {
            // Decision D8 (docs/todo/user-org-membership-i-fixes-B.md): distinct from
            // ASSUMPTION_NOT_PERMITTED - the assumption itself is still valid, so UserProvider
            // only notifies here, it does not clear the assumption or navigate away.
            // Issue #857 (item 6): unlike the branches above, the caller here stays mounted (it
            // is not being signed out or navigated away), so never settling left it spinning
            // forever instead of showing its own failure/retry UX (e.g.
            // UploadTrainingUsersModal, AddPurchasesUpload). Rejects with the same serializable
            // error shape as any other HTTP failure below, preserving body.code, after the
            // notify-only event above has already fired.
            window.dispatchEvent(new Event(ACCESS_AUDIT_UNAVAILABLE_EVENT));
            throw error;
        }
        if(response.status === 403 && responseBody?.code === 'READ_ONLY_ACCESS') {
            // View-only delegated access: notify only, the assumption stays valid. Issue #857
            // (item 6): same reasoning as ACCESS_AUDIT_UNAVAILABLE above - this is notify-only,
            // the caller stays mounted, so it must reject rather than hang.
            window.dispatchEvent(new Event(READ_ONLY_ACCESS_EVENT));
            throw error;
        }
        // Multipart upload endpoints report validation failures as 4xx with a `{ success: false,
        // errors }` body that their callers render; form() keeps returning that body as it did
        // before the shared handler.
        if(returnErrorBody && responseBody !== undefined) {
            return responseBody;
        }
        throw error;
    }
    // #883 (follow-up): wrapped in try/catch, mirroring the error branch above - previously a
    // bare `await response.json()` meant a parse rejection (a genuine bad body, or the body
    // stream erroring because the request was aborted/the connection dropped mid-read) propagated
    // immediately, bypassing the abort/stale-generation rechecks below entirely. Both outcomes of
    // the await now land here before either guard runs, so neither a swallowed-by-nothing
    // rejection nor a resolved value can slip past them.
    let result: any;
    let hasParseError = false;
    let parseError: unknown;
    try {
        result = await response.json();
    } catch(err) {
        hasParseError = true;
        parseError = err;
    }
    // #883 (security review, finding 2 + follow-up): re-checked after this body await too - the
    // entry-time abort guard above only covers an abort that landed before handleResponse() was
    // even entered; an abort that lands while this await is still pending (resolving OR
    // rejecting) must reject the same way, before returning the parsed body or the parse error.
    if(signal?.aborted) {
        throw new DOMException('The operation was aborted.', 'AbortError');
    }
    if(requestGeneration !== sessionGeneration) {
        // Issue #759 (security review): same reasoning as the check above, for the success
        // path - a response from an abandoned session must not repopulate Redux after the
        // reset (that is the same data-leak boundary #759 exists to close).
        return new Promise(() => {});
    }
    if(hasParseError) {
        // Ordinary (non-aborted, non-stale) parse failure - unchanged behavior, rethrown as-is.
        throw parseError;
    }
    return result;
}

function buildApi(getUserData: () => UserData | undefined) {
    const defaultHeaders = () => {
        const userData = getUserData();
        const headers: Record<string, string> = {};
        const assumedCompanyType = userData?.access?.assumedCompanyType;
        if(assumedCompanyType && userData?.companyId) {
            headers[`neoni-${assumedCompanyType}-assumed-company-id`] = userData.companyId;
        }
        return headers;
    }
    
    const request = async (path: string, method: string, body?: any, options?: RequestOptions) => {

        // Issue #759 (security review): captured before the request goes out, compared again once
        // it comes back - see handleResponse()'s generation checks above.
        const requestGeneration = sessionGeneration;

        // remove leading slashes on path
        path = path.replace(/^\/+/, '');
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            ...defaultHeaders(),
        };

        const response = await fetchAndSignal(API_ROOT + path, {
            method,
            body: JSON.stringify(body),
            headers,
            // Issue #857 (item 5): threaded through so logout's AbortController (lib/logout.ts)
            // can cancel its own POST once its timeout elapses - see fetchAndSignal above for how
            // an abort is distinguished from a real connectivity failure.
            signal: options?.signal,
        }, requestGeneration);
        return handleResponse(response, requestGeneration, false, options?.signal);
    };

    const get = async (path: string, options?: RequestOptions) => {
        return request(path, 'GET', undefined, options);
    };

    const post = async (path: string, body: any, options?: RequestOptions) => {
        return request(path, 'POST', body, options);
    };

    const put = async (path: string, body: any, options?: RequestOptions) => {
        return request(path, 'PUT', body, options)
    };

    const del = async(path: string, body: any, options?: RequestOptions) => {
        return request(path, 'DELETE', body, options)
    };

    const form = async(path: string, rawBody: any, options?: RequestOptions) => {

        // Issue #759 (final review F2): previously had none of request()'s session-expiry,
        // connectivity, or generation handling - a session expiry or lost connection mid-upload
        // surfaced as an unhandled/generic failure instead of the same shared sign-out/
        // connection-lost behaviour every other call site gets. Reuses the exact same shared
        // handler as request() above, per #759's "reuse the shared handler" scope.
        const requestGeneration = sessionGeneration;

        path = path.replace(/^\/+/, '');
        const response = await fetchAndSignal(API_ROOT + path, {
            method: 'POST',
            body: rawBody,
            headers: defaultHeaders(),
            signal: options?.signal,
        }, requestGeneration);
        return handleResponse(response, requestGeneration, true, options?.signal);
    }

    return {
        request,
        get,
        post,
        put,
        delete: del,
        form,
    };
}

export type NeoniApi = ReturnType<typeof buildApi>;

export default buildApi;
